import { Link } from "react-router-dom";
import { useState } from "react";
import {
    ShieldCheck, Sparkles, Shield, TrendingUp, Scale, Lock, Clock,
    Users, Stethoscope, CheckCircle2, ClipboardCheck, UserCog, Building2, Menu, X,
} from "lucide-react";
import { PhoenixWordmark } from "@/components/PhoenixLogo";
import { CareTeamInquiryDialog } from "@/components/CareTeamInquiryDialog";
import { DemoRequestDialog } from "@/components/DemoRequestDialog";
import { InstallAppCard, HeaderInstallButton } from "@/components/InstallAppCard";
import { TIDS } from "@/constants/testIds";

// Capability pillars — described at the OUTCOME level for decision-makers.
// Intentionally high-level: we sell what supervisors get, not how it's engineered.
const PILLARS = [
    {
        icon: Scale,
        title: "Defensible by design",
        desc: "Every required screening, assessment, and clinical decision is captured, timestamped, and attributable — a record built to hold up under the scrutiny that follows a serious incident.",
        tone: "text-indigo-600 bg-indigo-50",
    },
    {
        icon: ShieldCheck,
        title: "Evidence, already gathered",
        desc: "Rounds, meals, med passes, screenings, transfers and use-of-force all leave a timestamped trail, and the numbers behind them — response times, refusal streaks, overdue follow-ups, missed checks — are totalled as staff work. When someone asks what happened, the answer is already compiled.",
        tone: "text-teal-600 bg-teal-50",
    },
    {
        icon: Shield,
        title: "One live picture of risk",
        desc: "Clinical and custody signals come together so risk is seen and acted on across every shift — closing the gaps between disciplines where the most costly failures happen.",
        tone: "text-rose-600 bg-rose-50",
    },
    {
        icon: TrendingUp,
        title: "Better outcomes at release",
        desc: "Structured reentry preparation and benefits continuity that measurably support successful release — the outcomes your funders, courts, and community expect.",
        tone: "text-amber-600 bg-amber-50",
    },
];

// Why supervisors choose us — benefit framing, not a feature manual.
const WHY = [
    { icon: Scale, title: "Lower your liability exposure", desc: "Complete, defensible records built for how custody and clinical care are actually reviewed after an in-custody event." },
    { icon: Clock, title: "Stop chasing compliance", desc: "The system watches your obligations and surfaces gaps before they become audit findings — not after." },
    { icon: UserCog, title: "Deploy without disruption", desc: "Keep the clinicians you already trust, add licensed coverage from us, or run both. Your team, your way." },
    { icon: Lock, title: "Built for correctional security", desc: "HIPAA-compliant, role-based access, full audit trails, and a hardened posture appropriate for a secure environment." },
];

// Everyone in the facility works in one system — kept high-level.
const ROLES = [
    { icon: Building2, role: "Facility Leadership & HSAs", desc: "One live view of the metrics leadership gets asked for — round completion, response times, refusals, overdue follow-ups, transfers and use of force — exportable whenever funders, courts or oversight bodies come asking." },
    { icon: Shield, role: "Custody & Clinical Staff", desc: "A shared, real-time view of population risk, alerts, safety checks, and scheduling — so nothing falls between shifts or disciplines." },
    { icon: Stethoscope, role: "Providers & Nurses", desc: "The clinical workflows they need to document care and coordinate coverage — your staff or ours, in the same record." },
    { icon: Users, role: "Residents", desc: "Supervised, tablet-ready wellness and reentry tools that keep individuals engaged and reduce routine demand on staff." },
];

export default function LandingPage() {
    const [inquiryOpen, setInquiryOpen] = useState(false);
    const [demoOpen, setDemoOpen] = useState(false);
    const [navOpen, setNavOpen] = useState(false);
    return (
        <div className="min-h-screen bg-phoenix-mesh">
            <header className="sticky top-0 z-50 bg-white border-b border-slate-200/80 shadow-sm">
              <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                <PhoenixWordmark />
                <button type="button" data-testid="landing-mobile-menu-btn" aria-label="Menu"
                    onClick={() => setNavOpen((v) => !v)}
                    className="sm:hidden p-2 -mr-2 rounded-lg text-phoenix-navy hover:bg-phoenix-soft/50">
                    {navOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
                <nav className="hidden sm:flex items-center gap-2">
                    <HeaderInstallButton />
                    <button
                        type="button"
                        data-testid="header-request-demo-btn"
                        onClick={() => setDemoOpen(true)}
                        className="px-4 py-2 text-sm font-medium text-phoenix-navy hover:text-phoenix-orange transition-colors"
                    >
                        Request a demo
                    </button>
                    <Link
                        to="/login"
                        className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-phoenix-navy transition-colors"
                    >
                        Staff Sign-in
                    </Link>
                    <Link
                        to="/claim-access"
                        className="px-4 py-2 text-sm font-semibold bg-phoenix-navy text-white rounded-xl hover:bg-phoenix-navy-2 transition-colors"
                    >
                        I have an access code
                    </Link>
                </nav>
              </div>
              {navOpen && (
                <nav data-testid="landing-mobile-nav" className="sm:hidden border-t border-slate-200/80 bg-white px-6 py-3 flex flex-col gap-1">
                    <Link to="/login" data-testid="mobile-staff-signin"
                        className="px-3 py-3 rounded-xl text-sm font-semibold text-phoenix-navy hover:bg-phoenix-soft/50">Staff Sign-in</Link>
                    <Link to="/claim-access" data-testid="mobile-claim-access"
                        className="px-3 py-3 rounded-xl text-sm font-semibold bg-phoenix-navy text-white text-center hover:bg-phoenix-navy-2">I have an access code</Link>
                    <button type="button" data-testid="mobile-request-demo" onClick={() => { setNavOpen(false); setDemoOpen(true); }}
                        className="px-3 py-3 rounded-xl text-sm font-medium text-left text-phoenix-navy hover:bg-phoenix-soft/50">Request a demo</button>
                    <div className="px-1 pt-1"><HeaderInstallButton /></div>
                </nav>
              )}
            </header>

            <main className="max-w-6xl mx-auto px-6 pt-12 pb-24 grid lg:grid-cols-2 gap-12 items-center">
                <div data-testid={TIDS.landingHero} className="space-y-7 animate-fade-up">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-xs font-semibold text-phoenix-navy border border-border shadow-phoenix-card">
                        <Sparkles className="w-3.5 h-3.5 text-phoenix-orange" />
                        Timestamped · HIPAA-compliant · Built for the record
                    </div>

                    <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-phoenix-navy leading-[1.05] tracking-tight">
                        The Correctional Health Record That <span className="text-phoenix-orange">Protects Your Facility</span>
                    </h1>

                    <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                        Blue Phoenix unifies mental and medical health documentation, crisis oversight, and custody
                        coordination into one secure system — purpose-built for jails and prisons. Every check, med pass,
                        meal and transfer is timestamped and attributed, and the numbers behind them are counted as your
                        staff work.
                        <span className="font-semibold text-phoenix-navy"> One system. One record. The evidence already gathered.</span>
                    </p>

                    <div className="flex flex-wrap items-center gap-3">
                        <button type="button" onClick={() => setDemoOpen(true)} data-testid="hero-request-demo-btn"
                            className="inline-flex items-center gap-2 bg-phoenix-orange text-white px-7 py-3 rounded-2xl font-semibold hover:opacity-90 transition-opacity">
                            Request a confidential demo
                        </button>
                        <a href="mailto:partner@bluephnx.org" className="inline-flex items-center gap-2 text-phoenix-navy font-semibold px-4 py-3 hover:text-phoenix-orange transition-colors">
                            Talk to our team →
                        </a>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-2 p-6 bg-white rounded-2xl border border-border shadow-phoenix-card">
                        {[
                            { n: "1 in 4", l: "People in custody live with a serious mental illness" },
                            { n: "$1–3M", l: "Typical litigation cost of a single in-custody death" },
                            { n: "40–50%", l: "Recidivism reduction seen with structured reentry" },
                            { n: "Every entry", l: "Timestamped, attributed and reportable — from booking to release" },
                        ].map((s, i) => (
                            <div key={i} className="space-y-1 sm:border-l sm:first:border-l-0 sm:border-border/60 sm:pl-4 sm:first:pl-0">
                                <div className="font-display font-bold text-2xl text-phoenix-navy">{s.n}</div>
                                <div className="text-xs text-muted-foreground leading-relaxed">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Hero visual — a "record integrity" panel that speaks to leadership, not a feature reveal */}
                <div className="relative hidden lg:block animate-fade-up" style={{ animationDelay: "120ms" }}>
                    <div className="absolute inset-0 bg-phoenix-orange/10 blur-3xl rounded-full" aria-hidden />
                    <div className="relative bg-white rounded-3xl shadow-phoenix-deep p-7 border border-border/60">
                        <div className="flex items-center gap-3 pb-5 border-b border-border/60">
                            <div className="w-10 h-10 rounded-xl bg-phoenix-navy text-white flex items-center justify-center">
                                <ShieldCheck className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="font-semibold text-phoenix-navy">The record, as it happens</div>
                                <div className="text-xs text-muted-foreground">Counted and timestamped as staff work</div>
                            </div>
                        </div>
                        <div className="space-y-3 pt-5">
                            {[
                                "Intake & receiving screenings — tracked to completion",
                                "Suicide-risk checks — logged against their interval",
                                "Every action timestamped & attributable",
                                "Response times, refusals & overdue follow-ups — counted",
                            ].map((row) => (
                                <div key={row} className="flex items-center gap-3 text-sm text-phoenix-navy">
                                    <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                                    {row}
                                </div>
                            ))}
                        </div>
                        <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground bg-secondary/40 px-3 py-2 rounded-xl">
                            <Lock className="w-4 h-4 text-phoenix-navy" />
                            One record — defensible end to end, secured for the correctional environment.
                        </div>
                    </div>
                </div>
            </main>

            {/* Why leadership chooses Blue Phoenix */}
            <section data-testid="landing-features" className="bg-white border-y border-border/60">
                <div className="max-w-6xl mx-auto px-6 py-16">
                    <div className="text-center mb-10">
                        <div className="text-xs font-bold text-phoenix-orange uppercase tracking-widest mb-2">Why Facilities Choose Us</div>
                        <h2 className="font-display font-bold text-3xl text-phoenix-navy">Built Around What Keeps You Up at Night</h2>
                        <p className="text-muted-foreground text-base mt-3 max-w-2xl mx-auto">Liability, documentation, staffing, and safety — addressed in a single system your whole facility can stand behind.</p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {PILLARS.map((f) => (
                            <div key={f.title} className="bg-white border border-border/70 rounded-2xl p-6 hover:shadow-phoenix-card hover:border-phoenix-navy/30 transition-all">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${f.tone}`}>
                                    <f.icon className="w-5 h-5" />
                                </div>
                                <h3 className="font-semibold text-phoenix-navy mb-2">{f.title}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Staffing model — Bring Your Own Providers */}
            <section data-testid="landing-staffing-model" className="bg-phoenix-navy">
                <div className="max-w-6xl mx-auto px-6 py-16">
                    <div className="text-center mb-10">
                        <div className="text-xs font-bold text-phoenix-orange uppercase tracking-widest mb-2">Your Clinical Staff or Ours</div>
                        <h2 className="font-display font-bold text-3xl text-white">A Staffing Model That Fits Your Facility</h2>
                        <p className="text-white/60 text-base mt-3 max-w-2xl mx-auto">Keep the clinical team you already trust, add licensed telehealth coverage from us, or run both side by side. The record stays the same either way.</p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div data-testid="byo-providers-card" className="bg-white rounded-2xl p-7 border border-border/60 shadow-phoenix-card">
                            <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 text-phoenix-navy bg-secondary">
                                <UserCog className="w-5 h-5" />
                            </div>
                            <h3 className="font-display font-bold text-xl text-phoenix-navy mb-2">Bring Your Own Providers</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                Your nurses and clinicians work inside Blue Phoenix — no staffing change required.
                                Just cleaner documentation, tighter coordination, and complete audit trails.
                            </p>
                        </div>
                        <div data-testid="clinical-team-card" className="bg-white rounded-2xl p-7 border border-border/60 shadow-phoenix-card">
                            <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 text-phoenix-orange bg-orange-50">
                                <Building2 className="w-5 h-5" />
                            </div>
                            <h3 className="font-display font-bold text-xl text-phoenix-navy mb-2">Blue Phoenix Clinical Team</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                Need coverage? We provide licensed telehealth psychiatric and medical care directly —
                                credentialed, scheduled, and documented inside the same system you already run.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Who works in the system */}
            <section data-testid="landing-who-we-serve" className="max-w-6xl mx-auto px-6 py-16">
                <div className="text-center mb-10">
                    <div className="text-xs font-bold text-phoenix-orange uppercase tracking-widest mb-2">One System, Every Role</div>
                    <h2 className="font-display font-bold text-3xl text-phoenix-navy">From the Housing Unit to the Warden's Office</h2>
                    <p className="text-muted-foreground text-base mt-3 max-w-2xl mx-auto">Custody, clinical, leadership, and residents all work from the same source of truth — so information moves with the case, not the shift.</p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {ROLES.map((u) => (
                        <div key={u.role} className="bg-white border border-border/70 rounded-2xl p-6">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 text-phoenix-navy bg-secondary">
                                <u.icon className="w-5 h-5" />
                            </div>
                            <h3 className="font-semibold text-phoenix-navy mb-2">{u.role}</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">{u.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Why supervisors choose us — outcomes band */}
            <section data-testid="landing-why" className="bg-white border-y border-border/60">
                <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-x-12 gap-y-8">
                    {WHY.map((w) => (
                        <div key={w.title} className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-xl bg-phoenix-soft/60 text-phoenix-navy flex items-center justify-center shrink-0">
                                <w.icon className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-phoenix-navy mb-1">{w.title}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <InstallAppCard />

            {/* CTA */}
            <section data-testid="landing-cta" className="bg-phoenix-navy">
                <div className="max-w-3xl mx-auto px-6 py-16 text-center">
                    <h2 className="font-display font-bold text-3xl text-white mb-3">See What a Defensible Record Looks Like</h2>
                    <p className="text-white/70 text-base mb-7">A short, confidential walkthrough tailored to your facility — no obligation. See how leaders reduce risk, pass audits, and keep every resident's record complete.</p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        <button type="button" onClick={() => setDemoOpen(true)} data-testid="landing-request-demo-btn"
                            className="inline-block bg-phoenix-orange text-white px-7 py-3 rounded-2xl font-semibold hover:opacity-90 transition-opacity">
                            Request a confidential demo
                        </button>
                        <a href="mailto:partner@bluephnx.org" data-testid="landing-contact-btn"
                            className="inline-block bg-white/10 text-white ring-1 ring-white/30 px-7 py-3 rounded-2xl font-semibold hover:bg-white/20 transition-colors">
                            Contact Us
                        </a>
                    </div>
                </div>
            </section>

            <footer data-testid="landing-footer" className="bg-white border-t border-border/60">
                <div className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-[2fr_1fr_1fr] gap-8">
                    <div>
                        <PhoenixWordmark />
                        <p className="text-xs text-muted-foreground mt-3">The complete correctional health record — clinical care, crisis oversight &amp; custody coordination for jails and prisons.</p>
                        <p className="text-xs mt-2">
                            <a href="mailto:partner@bluephnx.org" className="text-phoenix-navy font-medium hover:text-phoenix-orange transition-colors">partner@bluephnx.org</a>
                        </p>
                    </div>
                    <div>
                        <h3 className="text-xs font-semibold text-phoenix-navy mb-3 uppercase tracking-wide">Partnering</h3>
                        <ul className="space-y-2 text-xs text-muted-foreground">
                            <li>County/City Jails</li>
                            <li>State Prisons</li>
                            <li>Tablet Companies</li>
                            <li><button type="button" data-testid="join-care-team-btn" onClick={() => setInquiryOpen(true)} className="hover:text-phoenix-orange transition-colors font-medium text-phoenix-navy">Join our care team →</button></li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-xs font-semibold text-phoenix-navy mb-3 uppercase tracking-wide">Legal</h3>
                        <ul className="space-y-2 text-xs text-muted-foreground">
                            <li><Link to="/privacy" className="hover:text-phoenix-orange transition-colors">Privacy Policy</Link></li>
                            <li><Link to="/terms" className="hover:text-phoenix-orange transition-colors">Terms of Service</Link></li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-border/60">
                    <div className="max-w-6xl mx-auto px-6 py-4 text-xs text-muted-foreground">
                        © 2026 Blue Phoenix · Built for correctional health · HIPAA Compliant · Tablet-Ready · Real-Time Crisis Alerts
                    </div>
                </div>
            </footer>
            <CareTeamInquiryDialog open={inquiryOpen} onClose={() => setInquiryOpen(false)} />
            <DemoRequestDialog open={demoOpen} onClose={() => setDemoOpen(false)} />
        </div>
    );
}
